import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { BudgetForm } from "@/components/admin/commercial/budget-form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Coins, Plus } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

interface BudgetRow {
  id: string;
  name: string;
  revenueCategory: string;
  budgetedAmount: string | null;
  currency: string;
  startDate: string;
  contract: { id: string; name: string } | null;
  service?: { id: string; customName?: string | null; product?: { name: string } } | null;
  client: { name: string };
}

export default function BudgetsPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: budgets, isLoading } = useQuery<BudgetRow[]>({
    queryKey: ["/api/budgets"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await apiRequest("POST", "/api/budgets", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/budgets"] });
      setDialogOpen(false);
      toast({ title: "Budget created" });
    },
  });

  const columns: ColumnDef<BudgetRow>[] = [
    {
      key: "name",
      header: "Budget",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.name}</span>
          <span className="text-xs text-muted-foreground">
            {(row.contract?.name || row.service?.customName || row.service?.product?.name || "Standalone")} · {row.client.name}
          </span>
        </div>
      ),
    },
    {
      key: "revenueCategory",
      header: "Revenue",
      cell: (row) => row.revenueCategory.replace(/_/g, " "),
    },
    {
      key: "budgetedAmount",
      header: "Amount",
      cell: (row) =>
        row.budgetedAmount ? formatCurrency(row.budgetedAmount, row.currency) : "—",
    },
    {
      key: "startDate",
      header: "Start",
      cell: (row) => formatDate(row.startDate),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Budgets"
        description="Recurring and project budgets underneath contracts"
        action={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Budget
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={budgets}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/budgets/${row.id}`)}
        emptyIcon={Coins}
        emptyTitle="No budgets yet"
        emptyDescription="Budgets appear once contracts start being broken into billable delivery areas."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Budget</DialogTitle>
          </DialogHeader>
          <BudgetForm
            onSubmit={(data) => createMutation.mutate(data)}
            isPending={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
